import React, { useState } from "react";
import { useAtom } from "jotai";
import { DocumentsOperations } from "constant-data/documentsData";
import axios from "axios";
import ExcelJS from "exceljs";
import _ from "lodash";
import moment from "moment";
import { useSetAtom } from "jotai/index";

import { Button, EButtonVariant } from "components/Button/Button";
import { HorizontalSpace } from "components/HorizontalSpace/HorizontalSpace";

import useDocuments from "../../hooks/useDocuments";
import {
  modal2Atom,
  modalAtom,
  setModal2CloseAtom,
  setModalCloseAtom,
  setSelectModalAtom,
} from "../../atoms/modalAtom/modalAtom";
import {
  docItemsToDrawAtom,
  openedDocumentAtom,
  selectedDocumentTypeAtom,
  selectedDocumentTypeTemplatesAtom,
} from "../../atoms/documentsAtom";

const Footer = () => {
  const { updateDocument, duplicateDocument } = useDocuments();
  const [, setSelectModal] = useAtom(setSelectModalAtom);
  const closeModal = useSetAtom(setModalCloseAtom);
  const [docItemsToDraw] = useAtom(docItemsToDrawAtom);
  const [openedDocument] = useAtom(openedDocumentAtom);
  const [selectedDocumentType] = useAtom(selectedDocumentTypeAtom);
  const [selectedDocumentTypeTemplates] = useAtom(
    selectedDocumentTypeTemplatesAtom
  );

  const onDuplicateClick = () => {
    setSelectModal({
      title: "Выберите тип нового документа",
      onSelectClick: duplicateDocument,
      atomToOpen: modal2Atom,
      setAtomClose: setModal2CloseAtom,
      options: DocumentsOperations.filter((i) => i.include).map((i) => ({
        value: i.id,
        label: i.name,
      })),
    });
  };

  const onPrintClick = () => {
    setSelectModal({
      title: "Выберите шаблон для печати",
      buttonText: "Напечатать",
      onSelectClick: onPrint,
      atomToOpen: modal2Atom,
      setAtomClose: setModal2CloseAtom,
      options:
        selectedDocumentTypeTemplates?.map((i) => ({
          value: i.template_id.toString(),
          label: i.title,
          file: i.file_path,
        })) || [],
    });
  };

  const onPrint = (val) => {
    if (!val?.file) return;
    replaceVariablesExcelJs(val.file).then();
  };

  const replacePlaceholders = (str, obj): string => {
    return str.replace(/%[^%]+%/g, (match) => {
      const key = match.slice(1, -1);
      if (key.startsWith("row")) return `%${key}%`;
      if (key === "time") return moment(obj.time).format("DD.MM.YYYY HH:mm:ss");

      return _.get(obj, key) ?? "-";
    });
  };

  const handleRows = (str, obj): string => {
    return str.replace(/%[^%]+%/g, (match) => {
      const key = match.slice(1, -1);
      if (!key.startsWith("row")) return `%${key}%`;

      return _.get(obj, key.slice(4)) ?? "-";
    });
  };

  const duplicateTableRows = (worksheet, countToDuplicate) => {
    let rowIndexOffset = 0; // Track new rows inserted
    let isInserted = false;
    worksheet.eachRow((row, rowIndex) => {
      if (isInserted) return;
      // Adjust the row index for new rows added
      const adjustedRowIndex = rowIndex + rowIndexOffset;

      // Check if any cell in the row contains the specific word
      let containsWord = false;
      row.eachCell((cell) => {
        if (cell.value && cell.value.toString().includes("%row")) {
          containsWord = true;
        }
      });

      if (containsWord) {
        // Duplicate the row
        let newRowIndex = 0;
        const rowIndexesToMerge: number[] = [];

        for (let i = 0; i < countToDuplicate; i++) {
          rowIndexesToMerge.push(rowIndex + i);
          newRowIndex = adjustedRowIndex + 1 + i;
          const newRow = worksheet.insertRow(newRowIndex, row.values);
          isInserted = true;

          // Copy styles from the original row to the new row
          row.eachCell({ includeEmpty: true }, (cell, colIndex) => {
            const newCell = newRow.getCell(colIndex);
            newCell.style = { ...cell.style }; // Copy cell style
            if (cell.isHyperlink) {
              newCell.value = { text: cell.text, hyperlink: cell.hyperlink }; // Copy hyperlink
            } else if (
              cell.value &&
              typeof cell.value === "object" &&
              cell.value.formula
            ) {
              newCell.value = { ...cell.value }; // Copy formulas
            }
          });

          // Handle merged cells
          if (worksheet.model.merges) {
            worksheet.model.merges.forEach((mergeRange) => {
              try {
                const [startCell, endCell] = mergeRange.split(":");
                const startRow = parseInt(startCell.replace(/\D/g, ""), 10);

                // Check if the merge applies to the current row
                if (startRow === rowIndex) {
                  const newStartCell = startCell.replace(startRow, newRowIndex);
                  const newEndCell = endCell.replace(startRow, newRowIndex);
                  const mergingGroup = `${newStartCell}:${newEndCell}`;

                  worksheet.mergeCells(mergingGroup);
                }
              } catch (e) {
                console.warn(e);
              }
            });
          }
        }

        rowIndexOffset++; // Increment offset for subsequent rows
      }
    });
  };

  const replaceVariablesExcelJs = async (fileUrl) => {
    if (!fileUrl) return;

    const response = await fetch(fileUrl);
    const arrayBuffer = await response.arrayBuffer();

    // @ts-ignore
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    // Iterate through all sheets
    workbook.eachSheet((worksheet) => {
      let docItemIndex = 0;
      const maxDocItemIndex = docItemsToDraw.length - 1;
      duplicateTableRows(worksheet, Math.max(docItemsToDraw.length - 1, 0));

      worksheet.eachRow((row, rowIndex) => {
        let rowModified = false;
        row.eachCell((cell, colIndex) => {
          if (typeof cell.value === "string") {
            // Replace simple variables
            cell.value = replacePlaceholders(cell.value, openedDocument);
            const updatedValue = handleRows(
              cell.value,
              docItemsToDraw[docItemIndex]
            );
            if (updatedValue !== cell.value) {
              cell.value = updatedValue;
              rowModified = true; // Mark that this row was modified for a doc item
            }
          }
        });

        // Increment docItemIndex only if the row was modified for a document item
        if (rowModified && docItemIndex < maxDocItemIndex) {
          docItemIndex++;
        }
      });
    });

    // Save the updated workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    // saveAs(blob, "UpdatedFile.xlsx");
    const url = URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = `fp-document-${selectedDocumentType?.id}.xlsx`;
    link.click();
  };

  return (
    <div className="flex flex-row justify-between items-center">
      <div className="flex flex-row justify-between items-center">
        <Button
          icon="galochka"
          variant={EButtonVariant.SECONDARY}
          onClick={() => updateDocument(false)}
        >
          Сохранить
        </Button>
        <HorizontalSpace size={11} />
        <Button
          icon="tickBlack"
          variant={EButtonVariant.PRIMARY}
          onClick={() => updateDocument(true)}
        >
          Ок
        </Button>
        <HorizontalSpace size={11} />
        <Button variant={EButtonVariant.PRIMARY} onClick={() => closeModal()}>
          Закрыть
        </Button>
        <HorizontalSpace size={11} />
        <Button
          icon="print"
          variant={EButtonVariant.PRIMARY}
          onClick={onPrintClick}
        >
          Печать
        </Button>
        <HorizontalSpace size={11} />
        <Button
          icon="download"
          variant={EButtonVariant.PRIMARY}
          onClick={onDuplicateClick}
        >
          Ввести на основании
        </Button>
      </div>
    </div>
  );
};

export default Footer;
